import { MFA_CHALLENGE_VIEW } from "@/utils/auth/mfaChallengeUrl";
import type { Database } from "@/utils/types";
import {
  BaseAppearance,
  BaseAuth,
  ViewType as SupabaseViewType,
} from "@supabase/auth-ui-shared";
import type { SupabaseClient } from "@supabase/supabase-js";
import { CSSProperties, ReactNode, type JSX } from "react";

type CustomAuthView = "sso" | typeof MFA_CHALLENGE_VIEW;

export type PasswordLoginMode = "turnstile" | "trustedIp";

export type ViewType = SupabaseViewType | CustomAuthView;

export const AUTH_VIEWS = {
  SIGN_IN: "sign_in",
  SIGN_UP: "sign_up",
  MAGIC_LINK: "magic_link",
  FORGOTTEN_PASSWORD: "forgotten_password",
  UPDATE_PASSWORD: "update_password",
  VERIFY_OTP: "verify_otp",
  SSO: "sso",
  MFA_CHALLENGE: MFA_CHALLENGE_VIEW,
} as const satisfies Record<string, ViewType>;

export const isAuthView = (view: string | null): view is ViewType =>
  Object.values(AUTH_VIEWS).some((authView) => authView === view);

export interface Appearance extends BaseAppearance {
  style?: {
    anchor?: CSSProperties;
    button?: CSSProperties;
    container?: CSSProperties;
    divider?: CSSProperties;
    input?: CSSProperties;
    label?: CSSProperties;
    loader?: CSSProperties;
    message?: CSSProperties;
  };
}

// BaseAuth types supabaseClient with the default `any` schema generic. Ours is
// always the typed browser client, so narrow it here rather than letting `any`
// reach the mutation hooks.
export interface Auth extends Omit<BaseAuth, "view" | "supabaseClient"> {
  supabaseClient: SupabaseClient<Database>;
  view?: ViewType;
  children?: ReactNode;
  appearance?: Appearance;
  title?: JSX.Element;
  skipBeforeUserCreateHookKey?: string;
  passwordLoginMode?: PasswordLoginMode;
  setAuthView: (view: ViewType) => void;
}
